import { TrackDef } from './spline';
import { Centerline } from './centerline';
import { autosmoothTrack } from './smooth';
import { applyForcedOrientations } from './orientation';
import { authoredRollKeysFromTrack } from './flyBank';
import { shortestAngleDelta } from './roll';
import { TrackWarning, ValidateOptions, validateTrack } from './validate';

const MAX_ITERS = 12;
/** smoothstep(t) peaks at 1.5× the average slope — plus a little margin. */
const SMOOTHSTEP_PEAK = 1.55;
const GROUND_MIN_Y = 0.5;

export interface AutoSolveResult {
  remaining: TrackWarning[];
}

function prepare(track: TrackDef): void {
  autosmoothTrack(track);
  applyForcedOrientations(track);
}

function currentWarnings(track: TrackDef, opts: ValidateOptions): TrackWarning[] {
  const cl = new Centerline(track);
  const keys = authoredRollKeysFromTrack(cl);
  return validateTrack(cl, keys, opts);
}

function isSolvable(w: TrackWarning): boolean {
  switch (w.kind) {
    case 'curvature-step':
    case 'roll-rate':
    case 'below-ground':
      return true;
    case 'min-radius':
    case 'g-vertical':
    case 'g-lateral':
    case 'rollback':
      return false;
    default: {
      const _never: never = w.kind;
      return _never;
    }
  }
}

function clearForcedOrientationAround(track: TrackDef, nodeIndex: number): void {
  const n = track.nodes.length;
  if (n === 0) return;
  const idxs = track.closed
    ? [nodeIndex, (nodeIndex + n - 1) % n, (nodeIndex + 1) % n]
    : [nodeIndex, nodeIndex - 1, nodeIndex + 1];
  for (const i of idxs) {
    if (i < 0 || i >= n) continue;
    const node = track.nodes[i];
    delete node.forcedYawDeg;
    delete node.forcedPitchDeg;
    node.mode = 'smooth';
  }
}

function liftAboveGround(track: TrackDef, minY: number): void {
  const cl = new Centerline(track);
  if (cl.totalLength <= 0) return;
  let lowest = Infinity;
  for (let s = 0; s <= cl.totalLength; s += 1) {
    lowest = Math.min(lowest, cl.posAt(s).y);
  }
  if (lowest >= minY) return;
  const lift = minY - lowest + 0.05;
  for (const n of track.nodes) n.p.y += lift;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

/**
 * Two-pass slope limiter on authored bank keys so interpolated roll (smoothstep)
 * stays within `maxRate` °/m.
 */
function limitRollRates(track: TrackDef, maxRate: number): void {
  const forced: number[] = [];
  for (let i = 0; i < track.nodes.length; i++) {
    if (track.nodes[i].forcedBankDeg !== undefined) forced.push(i);
  }
  if (forced.length < 2) return;

  const cl = new Centerline(track);
  const L = cl.totalLength;
  if (L <= 0) return;
  const maxSlope = maxRate / SMOOTHSTEP_PEAK;

  const sOf = (i: number) => cl.spanStartS(i);
  const deg = () => forced.map((i) => track.nodes[i].forcedBankDeg!);

  const write = (values: number[]) => {
    for (let k = 0; k < forced.length; k++) {
      track.nodes[forced[k]].forcedBankDeg = values[k];
    }
  };

  const slopeLimit = (values: number[], ss: number[]): number[] => {
    const out = values.slice();
    for (let i = 1; i < out.length; i++) {
      const maxD = maxSlope * Math.max(ss[i] - ss[i - 1], 1e-6);
      const delta = shortestAngleDelta(out[i - 1], out[i]);
      if (Math.abs(delta) > maxD) out[i] = out[i - 1] + Math.sign(delta) * maxD;
    }
    for (let i = out.length - 2; i >= 0; i--) {
      const maxD = maxSlope * Math.max(ss[i + 1] - ss[i], 1e-6);
      const delta = shortestAngleDelta(out[i + 1], out[i]);
      if (Math.abs(delta) > maxD) out[i] = out[i + 1] + Math.sign(delta) * maxD;
    }
    return out;
  };

  const ss = forced.map(sOf);
  write(slopeLimit(deg(), ss));

  if (!track.closed || forced.length < 2) return;

  for (let pass = 0; pass < 8; pass++) {
    const ia = forced[forced.length - 1];
    const ib = forced[0];
    const a = track.nodes[ia].forcedBankDeg!;
    const b = track.nodes[ib].forcedBankDeg!;
    const ds = Math.max(sOf(ib) + L - sOf(ia), 1e-6);
    const delta = shortestAngleDelta(a, b);
    const maxD = maxSlope * ds;
    if (Math.abs(delta) <= maxD + 1e-6) break;
    const reduce = (Math.abs(delta) - maxD) / 2;
    const sign = Math.sign(delta) || 1;
    track.nodes[ia].forcedBankDeg = a + sign * reduce;
    track.nodes[ib].forcedBankDeg = b - sign * reduce;
    write(slopeLimit(deg(), ss));
  }
}

/**
 * Mutate `track` so static geometry checks (curvature jumps, roll rate, ground)
 * are cleared where an automatic fix exists. Callers should run the usual
 * prepare/runtime rebuild afterwards.
 */
export function autoSolveTrackWarnings(track: TrackDef, opts: ValidateOptions = {}): AutoSolveResult {
  const maxRollRate = opts.maxRollRate ?? 6;
  prepare(track);
  let remaining = currentWarnings(track, opts);

  for (let iter = 0; iter < MAX_ITERS; iter++) {
    const todo = remaining.filter(isSolvable);
    if (todo.length === 0) return { remaining };

    const jumps = todo.filter((w) => w.kind === 'curvature-step');
    const needLift = todo.some((w) => w.kind === 'below-ground');
    const needRoll = todo.some((w) => w.kind === 'roll-rate');
    if (jumps.length === 0 && !needLift && !needRoll) return { remaining };

    for (const w of jumps) {
      const node = w.node ?? new Centerline(track).sToSpanU(w.s).span;
      clearForcedOrientationAround(track, node);
    }
    if (needLift) liftAboveGround(track, GROUND_MIN_Y);
    if (needRoll) limitRollRates(track, maxRollRate);

    prepare(track);
    remaining = currentWarnings(track, opts);
  }

  return { remaining };
}
